import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import ReactModal from "react-modal";
import { useQueryClient } from "@tanstack/react-query";

import UserSearchSelect from "../../components/inputs/UserSearchSelect";
import { currencyOptions } from "../../core/currency";

import { Badge, PageHeader, PageLayout, Select, StatePanel, Toggle } from "../../components/ui";
import OrderStatus from "../../components/order/Status";
import { SettingsContext, useEntitlements } from "../../contexts/index.jsx";
import { UserContext } from "../../contexts/User.jsx";
import { ACCOUNTING, canAccounting } from "../../helpers/accountingAccess";
import { getFileUrl } from "../../core/config/api.js";
import {
  useTransactionsPnl,
  downloadTransactionsPnlExcel,
  saveTransactionsPnlNote,
  saveTransactionsPnlFields,
  uploadTransactionsPnlDocument,
} from "../../hooks/dailyRecap/useTransactionsPnl";
import { buildColumns } from "./columns";

import "./transactions-pnl.scss";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const PX_COLORS = ["#7145D0", "#359DE8", "#F5BF4F", "#79E08F", "#ED6A5F"];

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.1;
const ZOOM_KEY = "pnlx_zoom";

const text = (v) => (v === null || v === undefined || v === "" ? "—" : v);

// Parse a backend-formatted money string ("1,234.56", "-500.00") or a number
// into a JS number. Returns null for blanks so totals/cells can show "—".
const parseMoney = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const round2 = (n) => Math.round(n * 100) / 100;

// Which column types are numeric (right-aligned, summed in the totals row).
// Why no VAT is due on a row. Only shown when a sale is NOT domestic — the
// figure is genuinely not applicable rather than nil.
const VAT_ROUTE_LABEL = {
  export: "Export — zero-rated",
  import: "Import",
  outside_scope: "Outside scope",
  unknown: "Unclassified",
};

const isNumCol = (col) =>
  ["money", "currency", "gbp", "pxcog", "costsTotal", "incomeGross", "vatOwed"].includes(col.type);

// A `finalisedOnly` column stays BLANK until the order's FINALISE COST button
// has been pressed. Rendering "—" there was actively misleading: it is the same
// glyph the table uses for a confirmed nil, so a row still being costed looked
// identical to one that genuinely had no transport or polishing spend.
const isHiddenUntilFinalised = (row, col) => col.finalisedOnly && !row.costs_finalised;

/**
 * Which Income Gross figure a row shows, and what to call it.
 *
 * A part-received sale (Deposited) has only its DEPOSIT inside `income_gross`
 * but the watch's FULL cost against it, so the booked figure comes out a large
 * negative that reads as a loss-making deal when all it means is "the balance
 * hasn't landed yet". The backend therefore also projects the margin the deal is
 * on course to make — the same formula with Previs standing in for cash received
 * — and says via `income_gross_basis` which of the two belongs on screen. The
 * label under the figure is what keeps the two readable apart in one column.
 *
 * Falls back to the booked figure when there is no projection (no sale price to
 * project from, and on PX trade-in rows, which have no sale of their own).
 */
const incomeGrossShown = (row) => {
  const provisional =
    row.income_gross_basis === "provisional" && row.income_gross_provisional != null;
  return {
    value: provisional ? row.income_gross_provisional : row.income_gross,
    label: provisional ? "Provisional" : "Margin",
  };
};

// Money moves over the wire in MINOR units (matching listings.* columns), but a
// person types major units. These two are the only place that conversion
// happens — going through a formatted display string instead is how an editor
// ends up saving 1.00 for 100.
const minorToMajorInput = (v) =>
  v === null || v === undefined || v === "" ? "" : String(Number(v) / 100);
const majorInputToMinor = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

// Currency codes are stored lower-case everywhere (see core/currency), while
// the column config historically declared them upper-case. Compare folded so
// the foreign-currency Buy Price columns actually match their rows — they never
// did before, which is why HKD/Dollars/Euros/CHF only ever showed "—".
const sameCurrency = (a, b) =>
  String(a || "gbp").toLowerCase() === String(b || "gbp").toLowerCase();

/**
 * Which single Buy Price cell hosts the editor: the column matching the row's
 * own buying currency. A row has ONE buy price — showing an input under all
 * five currencies would invite five different answers to one question.
 */
const isBuyPriceHost = (row, col) =>
  sameCurrency(row.editable?.buying_currency, col.currency);

/**
 * Shared save/refetch plumbing for every inline editor.
 *
 * Returns the current draft plus a `commit` that only fires when the value has
 * actually changed, so tabbing through a row doesn't write. On success the
 * whole month is invalidated rather than the one cell patched: a buy price or a
 * cost feeds Income Gross, the column footers and every KPI in the toolbar, all
 * of which are computed server-side. Patching locally would leave the row right
 * and the totals wrong.
 */
const useCellEditor = ({ listingId, field, value, onSaved }) => {
  const [draft, setDraft] = useState(value);
  const [state, setState] = useState(null); // null | "saving" | "saved" | "error"

  useEffect(() => {
    setDraft(value);
    setState(null);
  }, [value]);

  // Resolves true on success, false on failure, so editors holding their own
  // local text (the money ones) know to put the cell back.
  const commit = async (next = draft, extra = null) => {
    const patch = { [field]: next, ...(extra || {}) };
    const unchanged = next === value && !extra;
    if (unchanged) return true;
    setState("saving");
    try {
      await saveTransactionsPnlFields(listingId, patch);
      setState("saved");
      onSaved?.();
      return true;
    } catch (e) {
      console.error("P&L field save failed", e);
      setState("error");
      setDraft(value); // put the cell back to the last value the server confirmed
      return false;
    }
  };

  return { draft, setDraft, state, setState, commit };
};

/**
 * Money inputs keep the user's RAW TEXT while typing and convert only on
 * commit. Converting per keystroke destroys the decimal point: "12." parses to
 * 12, re-renders as "12", and the next digit lands in the pounds column — so
 * typing 12.50 produces 1250.00.
 */
const useMoneyText = (value) => {
  const [text, setText] = useState(minorToMajorInput(value));
  useEffect(() => { setText(minorToMajorInput(value)); }, [value]);
  return [text, setText, () => setText(minorToMajorInput(value))];
};

const cellClass = (state) => `pnlx-edit${state ? ` pnlx-edit--${state}` : ""}`;

/** Free text — PO/INV Number, Serial Number, Purchase Bank. */
const TextEditor = ({ listingId, field, value, onSaved }) => {
  const { draft, setDraft, state, commit } = useCellEditor({ listingId, field, value, onSaved });
  return (
    <input
      type="text"
      className={cellClass(state)}
      value={draft ?? ""}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit(draft === "" ? null : draft)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setDraft(value); e.currentTarget.blur(); }
      }}
    />
  );
};

/**
 * Money in the field's own currency. Minor units on the wire, major on screen.
 * The four Expenses columns are denominated in the listing's `costs_currency`,
 * which the P&L has no column for — so the code goes in the tooltip rather than
 * letting the "£" in the header speak for a figure that may not be pounds.
 */
const MoneyEditor = ({ listingId, field, value, currency, onSaved }) => {
  const { state, commit } = useCellEditor({ listingId, field, value, onSaved });
  const [text, setText, revert] = useMoneyText(value);
  return (
    <input
      type="number"
      step="0.01"
      min="0"
      className={`${cellClass(state)} pnlx-edit--num`}
      value={text}
      title={currency ? `Entered in ${String(currency).toUpperCase()}` : undefined}
      onChange={(e) => setText(e.target.value)}
      onBlur={async () => {
        if (!(await commit(majorInputToMinor(text)))) revert();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { revert(); e.currentTarget.blur(); }
      }}
    />
  );
};

/**
 * Native <select> — Stock type, VAT. Native on purpose: the table sits in a CSS
 * `zoom` wrapper, and a JS-positioned menu lands in the wrong place under zoom.
 * The browser's own popup does not.
 */
const SelectEditor = ({ listingId, field, value, options, onSaved }) => {
  const { draft, state, commit, setDraft } = useCellEditor({ listingId, field, value, onSaved });
  return (
    <select
      className={cellClass(state)}
      value={draft ?? ""}
      onChange={(e) => {
        const next = e.target.value === "" ? null : e.target.value;
        setDraft(next);
        commit(next);
      }}
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
};

/** Native date picker — Purchase Date. Commits on change, like the control does. */
const DateEditor = ({ listingId, field, value, onSaved }) => {
  const { draft, state, commit, setDraft } = useCellEditor({ listingId, field, value, onSaved });
  return (
    <input
      type="date"
      className={cellClass(state)}
      value={draft ?? ""}
      onChange={(e) => {
        const next = e.target.value === "" ? null : e.target.value;
        setDraft(next);
        commit(next);
      }}
    />
  );
};

/**
 * Buy price: amount + the currency it was paid in, saved together. Changing the
 * currency moves the figure to a different Buy Price column on the next
 * refresh, which is why both go in one request — saving them separately would
 * briefly record the old amount against the new currency.
 */
const BuyPriceEditor = ({ listingId, value, currency, onSaved }) => {
  const { state, commit } = useCellEditor({
    listingId, field: "buying_price", value, onSaved,
  });
  const [text, setText, revert] = useMoneyText(value);
  const [code, setCode] = useState(currency || "gbp");
  useEffect(() => { setCode(currency || "gbp"); }, [currency]);

  // Always include the row's OWN currency, even if currencyOptions() doesn't
  // offer it. With multi-currency off that list is the legacy five, which omits
  // CHF — and a <select> whose value isn't among its options renders blank,
  // so a CHF row would look uncurrencied and could be re-saved as something
  // else by a user who never meant to touch the currency.
  const options = useMemo(() => {
    const opts = currencyOptions();
    return opts.some((o) => sameCurrency(o.id, code))
      ? opts
      : [{ id: code, name: String(code).toUpperCase() }, ...opts];
  }, [code]);

  return (
    <span className="pnlx-edit-buy">
      <select
        className={cellClass(state)}
        value={code}
        onChange={(e) => {
          const next = e.target.value;
          setCode(next);
          // Amount and currency go in ONE request: saved separately, the row
          // would momentarily record the old amount against the new currency,
          // and the GBP pivot would be rebuilt from that.
          commit(majorInputToMinor(text), { buying_currency: next });
        }}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      <input
        type="number"
        step="0.01"
        min="0"
        className={`${cellClass(state)} pnlx-edit--num`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={async () => {
          if (!(await commit(majorInputToMinor(text)))) revert();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { revert(); e.currentTarget.blur(); }
        }}
      />
    </span>
  );
};

/**
 * Supplier picker. The only editor that can't be native: it needs async user
 * search. react-select can't be dropped straight into the table — it positions
 * its menu in JS and the table is inside a CSS `zoom` wrapper — so it opens in
 * a modal, which portals to <body>, outside the zoom.
 */
const SupplierEditor = ({ listingId, row, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const current = row.editable?.supplier_id
    ? { id: row.editable.supplier_id, name: row.sales_name || row.editable.supplier || "—" }
    : null;

  const pick = async (option) => {
    if (!option) return;
    setSaving(true);
    try {
      await saveTransactionsPnlFields(listingId, { supplier_id: option.id });
      setOpen(false);
      onSaved?.();
    } catch (e) {
      console.error("P&L supplier save failed", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button type="button" className="pnlx-edit pnlx-edit--picker" onClick={() => setOpen(true)}>
        {row.sales_name || row.editable?.supplier || "Set supplier…"}
      </button>
      <ReactModal
        isOpen={open}
        onRequestClose={() => setOpen(false)}
        portalClassName="payment-dialog-portal"
        style={{
          content: {
            top: "50%", left: "50%", right: "auto", bottom: "auto",
            marginRight: "-50%", transform: "translate(-50%, -50%)",
            borderRadius: "5px", border: "none", minWidth: "420px",
          },
        }}
      >
        <div className="pnlx-supplier-modal">
          <h4>Supplier</h4>
          <p>{row.brand} {row.model}{row.watch_reference ? ` · ${row.watch_reference}` : ""}</p>
          <UserSearchSelect
            value={current}
            defaultOptions={current ? [current] : undefined}
            placeholder="Search a supplier…"
            onChange={pick}
          />
          <div className="pnlx-supplier-modal__actions">
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>
              {saving ? "Saving…" : "Close"}
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

/**
 * The inline "Notes" cell. Own component with its own draft state so typing
 * re-renders one cell, not every row in the table.
 *
 * Saves on blur, and only when the text actually changed — a background refetch
 * every couple of minutes must not turn a click-through into a write. Escape
 * abandons the edit.
 */
const NoteCell = React.memo(function NoteCell({ listingId, value, onSave }) {
  const stored = value || "";
  const [draft, setDraft] = useState(stored);
  const [state, setState] = useState(null); // null | "saving" | "saved" | "error"

  // Re-seed only when the STORED note changes (a save elsewhere, or a period
  // switch). An identical string from a refetch leaves in-progress typing alone.
  useEffect(() => {
    setDraft(stored);
  }, [stored]);

  if (!listingId) return "—";

  const commit = async () => {
    if (draft === stored) return;
    setState("saving");
    try {
      await onSave(listingId, draft);
      setState("saved");
    } catch (e) {
      console.error("P&L note save failed", e);
      setState("error");
    }
  };

  return (
    <textarea
      className={`pnlx-note${state ? ` pnlx-note--${state}` : ""}`}
      value={draft}
      rows={2}
      placeholder="Add a note…"
      title={state === "error" ? "Couldn’t save — edit and click away to retry" : undefined}
      onChange={(e) => {
        setDraft(e.target.value);
        if (state) setState(null);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setDraft(stored);
          setState(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
});


/**
 * The PO/Invoice cell: always a "Link" to the row's purchase paperwork, or an
 * Upload button when there is none.
 *
 * The link's target is resolved SERVER-side from the documents that exist right
 * now (OrderExportService::poInvoiceDoc) — the Xero purchase order raised on the
 * linked Sell Request first, then whatever was uploaded into the listing's
 * CONTRACTS section. Nothing about it is stored on the row, which is what makes
 * the two directions symmetrical: uploading here files the document in
 * CONTRACTS ▸ Invoices and the cell becomes a link; deleting it there leaves
 * nothing to resolve and the Upload button comes back.
 *
 * `uploaded` is a local echo of the file just sent so the cell flips
 * immediately, before the month's refetch lands. It is dropped as soon as the
 * server's own answer for this row changes — including to null, so a document
 * removed in CONTRACTS doesn't leave a dead link behind.
 */
const PoInvoiceCell = React.memo(function PoInvoiceCell({ row, canUpload, onUploaded }) {
  const [uploaded, setUploaded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const serverUrl = row.po_invoice_url || null;

  useEffect(() => {
    setUploaded(null);
    setError(null);
  }, [serverUrl]);

  const url = serverUrl || uploaded?.po_invoice_url || null;
  const source = serverUrl ? row.po_invoice_source : uploaded?.po_invoice_source;

  if (url) {
    return (
      <a
        className="pnlx-doc"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={
          source === "xero_po"
            ? "Purchase order generated in Xero — opens in a new tab"
            : "Supplier document uploaded to the listing’s CONTRACTS section — opens in a new tab"
        }
      >
        Link
      </a>
    );
  }

  // No listing behind the row, so there is nothing to attach a file to. Only
  // reachable on rows whose listing was deleted after the order was placed.
  if (!row.listing_id) return "—";

  if (!canUpload) {
    return (
      <span className="pnlx-doc-none" title="No purchase order or invoice on file. Uploading one needs the P&L “Watch” edit permission.">
        —
      </span>
    );
  }

  const pick = async (e) => {
    const file = e.target.files?.[0];
    // Let the same file be chosen again after a failure — without this the
    // input's value is unchanged and onChange never fires a second time.
    e.target.value = "";
    if (!file) return;

    setBusy(true);
    setError(null);
    try {
      const data = await uploadTransactionsPnlDocument(row.listing_id, file);
      setUploaded(data);
      // The figures don't move, but the refetch is what proves the file
      // round-tripped through the listing rather than only into local state.
      onUploaded?.();
    } catch (err) {
      console.error("P&L PO/invoice upload failed", err);
      setError(
        err?.response?.data?.errors?.file?.[0] ||
          err?.response?.data?.message ||
          "Upload failed — try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="pnlx-doc-upload">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
        className="pnlx-doc-upload__input"
        onChange={pick}
      />
      <button
        type="button"
        className={`pnlx-doc-btn${error ? " pnlx-doc-btn--error" : ""}`}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        title={error || "Upload the supplier PO or invoice — it is filed in the listing’s CONTRACTS section"}
      >
        {busy ? "Uploading…" : error ? "Retry" : "Upload"}
      </button>
    </span>
  );
});
const TransactionsPnlBody = () => {
  const settings = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  // Plan gating: columns whose feature the store's plan lacks are dropped —
  // currently the Commissions breakdown (Feature 42). Figures already
  // recorded still count toward profit; only the column is hidden.
  const { hasFeature } = useEntitlements();
  const { groups, flatCols } = useMemo(() => buildColumns(hasFeature), [hasFeature]);
  const queryClient = useQueryClient();
  const qp = settings?.iframeQueryParams || "";
  const withQp = (path) => (qp ? `${path}?${qp}` : path);
  const now = new Date();

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [showCents, setShowCents] = useState(false);

  // Zoom: defaults to 60% (a zoomed-out overview); a saved preference wins.
  // The Fit button recomputes to fill the available width on demand.
  const savedZoom = parseFloat(localStorage.getItem(ZOOM_KEY));
  const [zoom, setZoom] = useState(() =>
    Number.isFinite(savedZoom) ? clamp(savedZoom, ZOOM_MIN, ZOOM_MAX) : 0.6,
  );
  const wrapRef = useRef(null);
  const tableRef = useRef(null);

  const { data, isLoading, isFetching, isError } = useTransactionsPnl(year, month);
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y, y - 1, y - 2];
  }, []);

  const pxColorMap = useMemo(() => {
    const map = {};
    let n = 0;
    rows.forEach((r) => {
      if (r.px_marker && !(r.px_marker in map)) {
        map[r.px_marker] = PX_COLORS[n % PX_COLORS.length];
        n += 1;
      }
    });
    return map;
  }, [rows]);

  // Persist zoom changes.
  useEffect(() => {
    localStorage.setItem(ZOOM_KEY, String(zoom));
  }, [zoom]);

  // Fit the table to the wrapper width. Both measurements are read from the
  // SAME element (the scroll container), so their ratio is the true horizontal
  // overflow at the current zoom regardless of how CSS `zoom` scales reported
  // px: newZoom = zoom × clientWidth / scrollWidth (capped at 1 — never upscale).
  const fitToWidth = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap || !wrap.scrollWidth) return;
    setZoom((z) => clamp(round2((z * wrap.clientWidth) / wrap.scrollWidth), ZOOM_MIN, 1));
  }, []);

  const onDownload = async () => {
    setDownloading(true);
    try {
      await downloadTransactionsPnlExcel(year, month);
    } catch (e) {
      console.error("P&L export failed", e);
    } finally {
      setDownloading(false);
    }
  };

  // Money formatters honour the cents toggle. gbp() takes a number (KPIs,
  // costs), money() takes a backend-formatted string (row cells).
  // INTENTIONALLY GBP: the P&L is a GBP-pivot report by design (the backend
  // sums gbp_* columns), so these stay '£' even under multi-currency — do
  // NOT switch them to the store base currency.
  const fmtGbp = useCallback(
    (n) =>
      n === null || n === undefined
        ? "—"
        : new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: showCents ? 2 : 0,
            maximumFractionDigits: showCents ? 2 : 0,
          }).format(n),
    [showCents],
  );
  const gbp = useCallback((v) => fmtGbp(typeof v === "number" ? v : parseMoney(v)), [fmtGbp]);
  const money = useCallback((v) => fmtGbp(parseMoney(v)), [fmtGbp]);

  // Type-aware deep link for the "Order" cell: order / sell / px-order, opened
  // in a new tab. Mirrors the Kanban rule (px wins, else request_type).
  const orderHref = (row) => {
    if (row.row_type === "px_sell") {
      return row.source_sell_request_id ? `/request/sell/${row.source_sell_request_id}` : null;
    }
    if (row.px_marker) return `/request/px-order/${row.px_marker}`;
    if (row.order_request_id) return `/request/order/${row.order_request_id}`;
    return null;
  };

  // Per-column totals (numeric columns only), computed once per row set.
  const columnTotals = useMemo(() => {
    const t = {};
    flatCols.forEach((c, i) => {
      if (!isNumCol(c)) return;
      let sum = 0;
      let has = false;
      rows.forEach((r) => {
        let val = null;
        // A column footer must equal the sum of the cells above it. Rows whose
        // Expenses cells are blanked pending FINALISE COST contribute nothing,
        // otherwise the total silently counts figures the table isn't showing.
        if (isHiddenUntilFinalised(r, c)) return;
        if (c.type === "currency") {
          // Folded compare, same as the cell renderer — an exact match here
          // would leave a foreign-currency column showing figures whose footer
          // stayed empty.
          if (sameCurrency(r.buy_currency, c.currency)) val = parseMoney(r.buy_original);
        } else if (c.type === "gbp") {
          val = parseMoney(r.buy_gbp);
        } else if (c.type === "pxcog") {
          val = parseMoney(r.px_cog);
        } else if (c.type === "costsTotal") {
          val = r.costs_total_raw != null ? r.costs_total_raw / 100 : null;
        } else if (c.type === "incomeGross") {
          // The DISPLAYED figure, so the footer still equals the column above
          // it. On a month holding deposited deals that makes it a mixed basis
          // — booked margin plus provisional — which is deliberate: the toolbar
          // MARGIN KPI stays cash-only and remains the reconcilable number.
          val = parseMoney(incomeGrossShown(r).value);
        } else if (c.type === "vatOwed") {
          // Footer total = VAT actually owed for the period. Nulls contribute
          // nothing, which is correct: PX trade-ins and non-domestic sales owe
          // nothing, so they must not drag the total.
          val = parseMoney(r.vat_owed);
        } else {
          val = parseMoney(r[c.key]);
        }
        if (val != null) {
          sum += val;
          has = true;
        }
      });
      t[i] = has ? sum : null;
    });
    return t;
  }, [rows]);

  const renderCommissions = (row) => {
    const list = row.commissions_detail;
    if (!Array.isArray(list) || list.length === 0) return "—";
    return (
      <span className="pnlx-comm">
        {list.map((c, i) => {
          const pct = c.profit_share_percentage ?? c.percentage;
          const pctLabel = pct == null ? "" : ` (${Math.round(Number(pct))}%)`;
          const amt = c.amount_raw != null ? c.amount_raw / 100 : parseMoney(c.amount);
          return (
            <span key={`${c.user_name}-${i}`} className="pnlx-comm__item">
              {c.user_name} = {fmtGbp(amt)}{pctLabel}
              {i < list.length - 1 ? <span className="pnlx-comm__sep"> | </span> : null}
            </span>
          );
        })}
      </span>
    );
  };

  // A refetch after every save: a cost or buy price feeds Income Gross, the
  // column footers and the toolbar KPIs, all computed server-side.
  const refetchRows = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["transactions-pnl", year, month] }),
    [queryClient, year, month],
  );

  /**
   * The edit descriptor for a cell, or null to render it read-only.
   *
   * Three things must hold: the column declares `edit`, the row is backed by a
   * listing to write to, and the user holds that section's grant. The server
   * re-checks the grant on every write — this only decides what to draw.
   */
  const editFor = (row, col) => {
    if (!col.edit || !row.listing_id || !row.editable) return null;
    if (!canAccounting(user, col.edit.grant)) return null;
    // One buy price per row: only the column matching its currency gets the editor.
    if (col.edit.kind === "buyPrice" && !isBuyPriceHost(row, col)) return null;
    // PO/INV Number shows the Xero purchase order when the listing's sell
    // request has one. That number belongs to Xero, and the editor writes
    // listings.invoice_number — a different field — so editing it would change
    // something other than what is on screen. Editable only on rows with no PO,
    // which are the legacy/imported ones the column falls back to.
    if (col.edit.field === "invoice_number" && row.has_purchase_order) return null;
    return col.edit;
  };

  const renderEditor = (row, col, edit) => {
    const listingId = row.listing_id;
    const raw = row.editable?.[edit.field];
    switch (edit.kind) {
      case "text":
        return <TextEditor listingId={listingId} field={edit.field} value={raw ?? ""} onSaved={refetchRows} />;
      case "money":
        return (
          <MoneyEditor
            listingId={listingId}
            field={edit.field}
            value={raw ?? null}
            currency={row.editable?.costs_currency}
            onSaved={refetchRows}
          />
        );
      case "select":
        return <SelectEditor listingId={listingId} field={edit.field} value={raw ?? null} options={edit.options} onSaved={refetchRows} />;
      case "date":
        return <DateEditor listingId={listingId} field={edit.field} value={raw ?? null} onSaved={refetchRows} />;
      case "buyPrice":
        return (
          <BuyPriceEditor
            listingId={listingId}
            value={row.editable?.buying_price ?? null}
            currency={row.editable?.buying_currency}
            onSaved={refetchRows}
          />
        );
      case "supplier":
        return <SupplierEditor listingId={listingId} row={row} onSaved={refetchRows} />;
      default:
        return null;
    }
  };

  const cellContent = (row, col) => {
    switch (col.type) {
      case "image": {
        const url = getFileUrl(row.stock_image_file_id);
        return url ? (
          <img src={url} alt={row.watch_reference || "watch"} className="pnlx-thumb" />
        ) : (
          <span className="pnlx-thumb pnlx-thumb--placeholder" />
        );
      }
      case "badge":
        return (
          <Badge tone={row.row_type === "px_sell" ? "accent" : "success"} size="sm">
            {row.row_type === "px_sell" ? "PX-Sell" : "Sale"}
          </Badge>
        );
      case "link-order":
        return row.order_ref_number ? (
          <Link to={withQp(`/order/${row.order_ref_number}`)}>{row.order_ref_number}</Link>
        ) : "—";
      case "link-order-request": {
        const href = orderHref(row);
        const label = row.row_type === "px_sell" ? row.source_sell_request_id : row.order_request_id;
        if (!label) return "—";
        return href ? (
          <a href={withQp(href)} target="_blank" rel="noopener noreferrer" title="Open request in a new tab">
            #{label}
          </a>
        ) : `#${label}`;
      }
      case "link-ref":
        return row.listing_id ? (
          <Link to={withQp(`/product/${row.listing_id}/edit/product`)} title="Open watchcard to add cost">
            {row.watch_reference || "—"}
          </Link>
        ) : text(row.watch_reference);
      case "stockType":
        return row.stock_type ? <Badge tone="accent" size="sm">{row.stock_type}</Badge> : "—";
      // Country as a flag, not an ISO code. The backend emits alpha-2 (the same
      // `alpha2Code` the rest of the app feeds ReactCountryFlag), so the code
      // itself moves to the tooltip/aria-label — it is still readable, just not
      // occupying a column at 60% zoom.
      case "flag": {
        const code = row[col.key];
        if (!code) return "—";
        return (
          <ReactCountryFlag
            countryCode={String(code).toUpperCase()}
            svg
            title={String(code).toUpperCase()}
            aria-label={String(code).toUpperCase()}
            className="pnlx-flag"
          />
        );
      }
      case "currency":
        // Show the buy price under its own currency column only. Folded compare:
        // codes are stored lower-case, the column config declares them however
        // it likes, and an exact match silently blanked every foreign-currency
        // buy price.
        return sameCurrency(row.buy_currency, col.currency) ? money(row.buy_original) : "—";
      case "gbp":
        return money(row.buy_gbp);
      case "pxcog":
        return money(row.px_cog);
      case "costsTotal":
        if (isHiddenUntilFinalised(row, col)) return "";
        return row.costs_total_raw != null ? gbp(row.costs_total_raw / 100) : "—";
      case "note":
        return (
          <NoteCell listingId={row.listing_id} value={row.pnl_note} onSave={saveTransactionsPnlNote} />
        );
      // Always a link or an Upload button — see PoInvoiceCell.
      case "document":
        return (
          <PoInvoiceCell
            row={row}
            canUpload={canAccounting(user, ACCOUNTING.PNL_EDIT_WATCH)}
            onUploaded={refetchRows}
          />
        );
      // Margin, with the basis that produced it underneath — booked margin and
      // provisional margin share this column, and a bare number cannot say which
      // one it is (see incomeGrossShown).
      case "incomeGross": {
        const { value, label } = incomeGrossShown(row);
        const n = parseMoney(value);
        const neg = n != null && n < 0;
        return (
          <span className="pnlx-margin">
            <span className={neg ? "pnlx-neg" : "pnlx-pos"}>{money(value)}</span>
            {/* No figure, no basis to name — a label under a bare "—" would be
                claiming something about a number that isn't there. */}
            {n === null ? null : (
              <span className={`pnlx-margin__basis${label === "Provisional" ? " pnlx-margin__basis--provisional" : ""}`}>
                {label}
              </span>
            )}
          </span>
        );
      }
      // VAT owed to HMRC, with the basis that produced it underneath — the
      // figure alone is ambiguous, since the same column holds margin-scheme
      // and standard-VAT numbers computed by different formulas.
      //
      // No figure means one of two different things, so they read differently:
      // a non-domestic sale shows the ROUTE (nothing is due, and why); a PX
      // trade-in row or an unmodelled jurisdiction shows nothing at all.
      case "vatOwed": {
        if (row.vat_owed == null) {
          const r = row.trade_route;
          if (!r || r === "domestic") return "";
          return <span className="pnlx-vat"><span className="pnlx-vat__route">{VAT_ROUTE_LABEL[r] || r}</span></span>;
        }
        return (
          <span className="pnlx-vat">
            {money(row.vat_owed)}
            <span className="pnlx-vat__basis">
              {row.vat_scheme === "vat" ? "VAT" : "Margin Scheme"}
            </span>
          </span>
        );
      }
      case "commissions":
        return renderCommissions(row);
      case "status":
        return row.order_or_px_request_status ? (
          <OrderStatus status={row.order_or_px_request_status} />
        ) : "—";
      case "reconciliation":
        return row.revenue_reconciled
          ? <Badge tone="success" size="sm">✓ Reconciled</Badge>
          : <Badge tone="warning" size="sm">No</Badge>;
      case "purchaseReconciliation":
        return row.purchase_reconciled
          ? <Badge tone="success" size="sm">✓ Reconciled</Badge>
          : <Badge tone="warning" size="sm">No</Badge>;
      case "costReconciliation":
        return row.cost_reconciled
          ? <Badge tone="success" size="sm">✓ Reconciled</Badge>
          : <Badge tone="warning" size="sm">No</Badge>;
      case "money":
        if (isHiddenUntilFinalised(row, col)) return "";
        return money(row[col.key]);
      case "date":
      case "text":
      default:
        return text(row[col.key]);
    }
  };

  return (
    <PageLayout className="pnlx-page">
      {/* M59A — the shared chrome. Same handlers, same payloads: Download Excel still calls
          downloadTransactionsPnlExcel(year, month), Reconcile still navigates to /reconciliation
          (with the iframe query params when present). Not sticky: the page never scrolls — the
          table wrap is the only scroller, so a sentinel would have nothing to observe. */}
      <PageHeader
        title="Transactions P&L"
        showBackButton={false}
        sticky={false}
        actions={[
          {
            key: "download",
            label: downloading ? "Preparing…" : "Download Excel",
            onClick: onDownload,
            disabled: downloading || isLoading,
            variant: "primary",
          },
          { key: "reconcile", label: "Reconcile", to: withQp("/reconciliation"), variant: "secondary" },
        ]}
      />
      <div className="pnlx-toolbar">
        <div className="pnlx-toolbar__left">
          {/* searchable={false}: 12 months trip Select's >8-options auto-search, and a month
              picker doesn't need a text filter. */}
          <Select
            label="Month"
            searchable={false}
            options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))}
            value={month}
            onChange={(v) => setMonth(Number(v))}
          />
          <Select
            label="Year"
            searchable={false}
            options={years.map((y) => ({ value: y, label: String(y) }))}
            value={year}
            onChange={(v) => setYear(Number(v))}
          />
          <Toggle size="sm" label="cents" checked={showCents} onChange={setShowCents} />
          <div className="pnlx-zoom" role="group" aria-label="Zoom">
            <button type="button" className="pnlx-zoom__btn" onClick={() => setZoom((z) => clamp(round2(z - ZOOM_STEP), ZOOM_MIN, ZOOM_MAX))} aria-label="Zoom out" title="Zoom out">−</button>
            <button type="button" className="pnlx-zoom__val" onClick={fitToWidth} title="Fit to width">{Math.round(zoom * 100)}%</button>
            <button type="button" className="pnlx-zoom__btn" onClick={() => setZoom((z) => clamp(round2(z + ZOOM_STEP), ZOOM_MIN, ZOOM_MAX))} aria-label="Zoom in" title="Zoom in">+</button>
          </div>
          {isFetching && <span className="pnlx-loading">Updating…</span>}
        </div>
        <div className="pnlx-toolbar__right">
          <div className="pnlx-kpi"><span>Sold</span><b>{totals.watches_sold ?? 0}</b></div>
          <div className="pnlx-kpi"><span>Revenue</span><b>{gbp(totals.total_sell)}</b></div>
          <div className="pnlx-kpi"><span>Margin</span><b className={(totals.gross_margin ?? 0) < 0 ? "pnlx-neg" : "pnlx-accent"}>{gbp(totals.gross_margin)}</b></div>
          <div className="pnlx-kpi"><span>Cost/watch</span><b>{gbp(totals.cost_per_watch)}</b></div>
          <div className="pnlx-kpi"><span>Profit/watch</span><b className={(totals.profit_per_watch ?? 0) < 0 ? "pnlx-neg" : "pnlx-accent"}>{gbp(totals.profit_per_watch)}</b></div>
        </div>
      </div>

      {isLoading ? (
        <StatePanel state="loading" loadingShape="row" loadingCount={6} />
      ) : isError ? (
        // Retry re-runs the read only — it invalidates the month's query, exactly what a save does.
        <StatePanel state="error" title="Couldn’t load transactions" description="Try again." onRetry={refetchRows} />
      ) : rows.length === 0 ? (
        <StatePanel state="empty" title="No transactions" description={`No transactions for ${MONTHS[month - 1]} ${year}.`} />
      ) : (
        <div className="pnlx-table-wrap" ref={wrapRef} style={{ zoom }}>
          <table className="pnlx-table" ref={tableRef}>
            <colgroup>
              {flatCols.map((c) => (
                <col key={c.key + c.label} style={{ width: c.w ? `${Math.round(c.w * 1.4)}px` : undefined }} />
              ))}
            </colgroup>
            <thead>
              <tr className="pnlx-grouprow">
                {groups.map((g, gi) => (
                  <th
                    key={`g-${gi}`}
                    colSpan={g.cols.length}
                    className={`pnlx-group pnlx-group-end ${g.label ? `pnlx-group--${gi % 6}` : "pnlx-group--blank"}`}
                  >
                    {g.label}
                  </th>
                ))}
              </tr>
              <tr className="pnlx-headrow">
                {flatCols.map((c) => (
                  <th
                    key={c.key + c.label}
                    className={`${isNumCol(c) ? "pnlx-num" : ""}${c.groupEnd ? " pnlx-group-end" : ""}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                // Highlight the whole PX group (PX Order + PX Sell rows share a
                // px_marker) with the neutral hover-grey rather than tinting only
                // the PX-Sell row purple.
                const inPxGroup = !!r.px_marker;
                const stripe = r.px_marker ? pxColorMap[r.px_marker] : null;
                return (
                  <tr
                    key={`${r.row_type}-${r.listing_id || "x"}-${r.order_ref_number || ""}-${idx}`}
                    className={`pnlx-row${inPxGroup ? " pnlx-row--pxgroup" : ""}`}
                    style={stripe ? { borderLeft: `3px solid ${stripe}` } : undefined}
                  >
                    {flatCols.map((c) => {
                      const edit = editFor(r, c);
                      return (
                        <td
                          key={c.key + c.label}
                          className={`${isNumCol(c) ? "pnlx-num" : ""}${c.groupEnd ? " pnlx-group-end" : ""}${edit ? " pnlx-cell--editable" : ""}`}
                        >
                          {edit ? renderEditor(r, c, edit) : cellContent(r, c)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="pnlx-totalrow">
                {flatCols.map((c, i) => {
                  const tot = columnTotals[i];
                  const neg = c.type === "incomeGross" && tot != null && tot < 0;
                  return (
                    <td
                      key={c.key + c.label}
                      className={`${isNumCol(c) ? "pnlx-num" : ""}${c.groupEnd ? " pnlx-group-end" : ""}${neg ? " pnlx-neg" : ""}`}
                    >
                      {i === 0 ? `Total (${rows.length})` : tot != null ? gbp(tot) : ""}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </PageLayout>
  );
};

// Gate the page on the "P&L view" grant
// (master admins pass by default; other roles need the explicit grant).
const TransactionsPnlPage = () => {
  const { user } = useContext(UserContext);
  if (!canAccounting(user, ACCOUNTING.PNL)) {
    return (
      <PageLayout className="pnlx-page">
        <StatePanel state="permission" title="Access restricted" />
      </PageLayout>
    );
  }
  return <TransactionsPnlBody />;
};

export default TransactionsPnlPage;
